import React from 'react';
import { Card } from '@mui/material';

// Import our custom component
import QuestionArea from './QuestionArea';

/**
 * SurveySection Component
 * 
 * Represents a single section of the survey (e.g., "Kitchen", "Dining Area").
 * Each section contains multiple question areas that are grouped by topic.
 * 
 * This component:
 * - Groups questions by area within the section
 * - Renders each area as a separate QuestionArea component
 * - Provides consistent styling and layout
 * 
 * @param {Object} props - Component props
 * @param {Object} props.control - React Hook Form control object
 * @param {Object} props.errors - Form validation errors
 * @param {Array} props.sectionInfo - Array of questions in this section
 * @param {string} props.sectionKey - The section name/key
 * @param {number} props.sectionIndex - Zero-based section index
 */
const SurveySection = ({
  control,
  errors,
  sectionInfo,
  sectionKey,
  sectionIndex,
}) => {
  /**
   * Groups questions by their area property
   * This creates an object where keys are area names and values are arrays of questions
   */
  const groupQuestionsByArea = (questions) => {
    // Guard clause: ensure questions is an array
    if (!questions || !Array.isArray(questions)) {
      return {};
    }
    
    const grouped = {};
    
    questions.forEach((question) => {
      const areaKey = question.area;
      
      if (!grouped[areaKey]) {
        grouped[areaKey] = [];
      }
      
      grouped[areaKey].push(question);
    });
    
    return grouped;
  };

  // Group the section's questions by area
  const groupedQuestions = groupQuestionsByArea(sectionInfo);
  const areaKeys = Object.keys(groupedQuestions);

  /**
   * Renders all question areas within this section
   */
  const renderQuestionAreas = () => {
    return areaKeys.map((areaKey, areaIndex) => {
      const areaQuestions = groupedQuestions[areaKey];
      
      return (
        <QuestionArea
          key={`${sectionKey}-${areaKey}`}
          control={control}
          errors={errors}
          questions={areaQuestions}
          areaKey={areaKey}
          areaIndex={areaIndex}
          sectionIndex={sectionIndex}
          sectionKey={sectionKey}
        />
      );
    });
  };

  return (
    <Card
      style={{
        height: '100%',
        // Removed maxHeight and overflowY scroll to allow natural content flow
        // maxHeight: 600,
        // overflowY: 'scroll'
      }}
    >
      {renderQuestionAreas()}
    </Card>
  );
};

export default SurveySection;